import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, X, CheckCircle, Pencil, Save, AlertTriangle } from "lucide-react";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { useToast } from "@/hooks/use-toast";
import { useApp } from "@/contexts/AppContext";
import { useUsers } from "@/contexts/UsersContext";
import { ROLE_TEMPLATES } from '@/types/permissions';
import { wouldUserRoleChangeCauseLockout, LOCKOUT_ERROR_MESSAGE } from '@/utils/lockoutProtection';
import { updateUserRole } from "@/api/services/userService";
import { getRoles } from "@/api/services/roleService";

interface User {
  id: string;
  name: string;
  email: string;
  systemRole: 'Administrator' | 'User' | 'External User';
  forecasterRole: string;
  roleId: number;
  jurisdictionIds: string[];
}

// DEMO_USERS has moved to UsersContext — single source of truth.

interface UserManagementProps {
  roleFilter?: string;
  onRoleFilterConsumed?: () => void;
}

export function UserManagement({ roleFilter: initialRoleFilter, onRoleFilterConsumed }: UserManagementProps = {}) {
  const { toast } = useToast();
  const { mode } = useApp();
  const { getAvatarUrlByName } = useUserPreferences();
  const queryClient = useQueryClient();
  const isLive = mode === 'live';

  // Users list — sourced from UsersContext (single source of truth).
  const { users: contextUsers, setUsers: setContextUsers } = useUsers();

  // Map AppUser → local User shape
  const users: User[] = useMemo(() => contextUsers.map(u => ({
    id: u.id,
    name: u.name,
    email: u.email,
    systemRole: u.systemRole,
    forecasterRole: u.forecasterRole,
    roleId: 0,
    jurisdictionIds: [],
  })), [contextUsers]);

  const applyUsers = (updatedUsers: User[]) => {
    setContextUsers(updatedUsers.map(u => ({
      id: u.id,
      name: u.name,
      email: u.email,
      forecasterRole: u.forecasterRole,
      systemRole: u.systemRole,
    })));
  };

  const { data: apiRolesData } = useQuery({
    queryKey: ['system-roles'],
    queryFn: getRoles,
    enabled: isLive,
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: number }) =>
      updateUserRole(userId, { roleId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });

  const availableForecasterRoles = useMemo(() => {
    if (isLive && apiRolesData) {
      return apiRolesData.roles.map(role => ({ id: role.roleId, name: role.roleName }));
    }
    return ROLE_TEMPLATES.map(role => ({ id: 0, name: role.name }));
  }, [isLive, apiRolesData]);

  // ── Draft state: pending role changes (userId → new role name) ───────────
  // Changes are held here until the user clicks Save. No immediate application.
  const [draftRoles, setDraftRoles] = useState<Record<string, string>>({});
  const hasDraftChanges = Object.keys(draftRoles).length > 0;

  // The effective role for a user: draft override if present, else committed role
  const effectiveRole = (user: User) => draftRoles[user.id] ?? user.forecasterRole;

  // Stage a role change into draft — no validation yet
  const handleRoleChange = (userId: string, newRole: string) => {
    const user = users.find(u => u.id === userId);
    // If the draft is being reverted to the original, remove it from draft
    if (user && user.forecasterRole === newRole) {
      setDraftRoles(prev => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    } else {
      setDraftRoles(prev => ({ ...prev, [userId]: newRole }));
    }
  };

  // Discard all pending changes
  const handleDiscardChanges = () => {
    setDraftRoles({});
  };

  // Validate and apply all draft changes on Save
  const handleSave = () => {
    // Run lockout protection across all pending changes
    for (const [userId, newRole] of Object.entries(draftRoles)) {
      const newRoleTemplate = ROLE_TEMPLATES.find(r => r.name === newRole);
      if (newRoleTemplate && wouldUserRoleChangeCauseLockout(userId, newRoleTemplate.id, mode)) {
        const user = users.find(u => u.id === userId);
        toast({
          title: "Cannot Save Changes",
          description: `Cannot change ${user?.name ?? 'this user'}'s role to ${newRole}. ${LOCKOUT_ERROR_MESSAGE}`,
          variant: "destructive",
        });
        return;
      }
    }

    const changedCount = Object.keys(draftRoles).length;

    if (isLive) {
      // Apply each change via API
      for (const [userId, newRole] of Object.entries(draftRoles)) {
        const role = availableForecasterRoles.find(r => r.name === newRole);
        if (role) {
          updateRoleMutation.mutate({ userId, roleId: role.id });
        }
      }
    }

    // Apply all draft changes to the committed user list
    const updatedUsers = users.map(u =>
      draftRoles[u.id] ? { ...u, forecasterRole: draftRoles[u.id] } : u
    );
    applyUsers(updatedUsers);
    setDraftRoles({});

    toast({
      title: "Changes Saved",
      description: `${changedCount} role assignment${changedCount !== 1 ? 's' : ''} updated successfully.`,
    });
  };

  // ── Dialog states ─────────────────────────────────────────────────────────
  const [bulkRoleChangeDialogOpen, setBulkRoleChangeDialogOpen] = useState(false);

  // ── Search and filter ─────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>(initialRoleFilter ?? 'all');

  useEffect(() => {
    if (initialRoleFilter) {
      setRoleFilter(initialRoleFilter);
      onRoleFilterConsumed?.();
    }
  }, [initialRoleFilter, onRoleFilterConsumed]);

  // ── Bulk selection ────────────────────────────────────────────────────────
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [bulkRole, setBulkRole] = useState('User');

  // Filter users based on search and effective (draft-aware) role filter
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || effectiveRole(user) === roleFilter;
    return matchesSearch && matchesRole;
  });

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUserIds(filteredUsers.map(u => u.id));
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleSelectUser = (userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUserIds([...selectedUserIds, userId]);
    } else {
      setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
    }
  };

  // Bulk role change — stages into draft, no immediate application
  const handleBulkRoleChange = (role: string) => {
    if (selectedUserIds.length === 1) {
      handleRoleChange(selectedUserIds[0], role);
      setSelectedUserIds([]);
      return;
    }
    setBulkRole(role);
    setBulkRoleChangeDialogOpen(true);
  };

  const confirmBulkRoleChange = () => {
    const newDraft: Record<string, string> = { ...draftRoles };
    for (const userId of selectedUserIds) {
      const user = users.find(u => u.id === userId);
      if (user && user.forecasterRole === bulkRole) {
        delete newDraft[userId];
      } else {
        newDraft[userId] = bulkRole;
      }
    }
    setDraftRoles(newDraft);
    setBulkRoleChangeDialogOpen(false);
    setSelectedUserIds([]);
  };

  const handleClearSelection = () => {
    setSelectedUserIds([]);
  };

  // Get count of users for each role using effective (draft-aware) roles
  const getRoleCount = (roleName: string) => {
    return users.filter(u => effectiveRole(u) === roleName).length;
  };

  return (
    <div className="space-y-6 p-6">


      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Users</CardTitle>
            <p className="text-sm text-muted-foreground">
              {filteredUsers.length === users.length
                ? `Showing ${users.length} user${users.length !== 1 ? 's' : ''}`
                : `Showing ${filteredUsers.length} of ${users.length} user${users.length !== 1 ? 's' : ''}`
              }
            </p>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filter Controls */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[240px]">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {availableForecasterRoles.map(role => (
                  <SelectItem key={role.name} value={role.name}>
                    {role.name} ({getRoleCount(role.name)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Pending changes banner */}
          {hasDraftChanges && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm font-medium">
                  {Object.keys(draftRoles).length} unsaved role change{Object.keys(draftRoles).length !== 1 ? 's' : ''} — click Save to apply
                </span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button variant="outline" size="sm" onClick={handleDiscardChanges}>
                  Discard
                </Button>
                <Button size="sm" onClick={handleSave}>
                  <Save className="h-3.5 w-3.5 mr-1.5" />
                  Save Changes
                </Button>
              </div>
            </div>
          )}

          {/* Bulk Actions Toolbar */}
          {selectedUserIds.length > 0 && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <span className="font-medium text-blue-900 dark:text-blue-100">
                    {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-blue-900 dark:text-blue-100">Change Role:</span>
                  <Select value={bulkRole} onValueChange={handleBulkRoleChange}>
                    <SelectTrigger className="w-[200px] bg-white dark:bg-gray-900">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableForecasterRoles.map(role => (
                        <SelectItem key={role.name} value={role.name}>{role.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={handleClearSelection}>
                <X className="h-4 w-4 mr-2" />
                Clear Selection
              </Button>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={selectedUserIds.length === filteredUsers.length && filteredUsers.length > 0}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all users"
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Evo Role</TableHead>
                <TableHead>
                  <div className="flex items-center gap-2">
                    Forecaster Role
                    <Pencil className="h-3 w-3 text-muted-foreground" />
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No users found matching your criteria
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => {
                  // Marcus Patel is disabled for Release 1 — Regional Manager role not active
                  const isDisabledR1 = user.id === '5';
                  const isDraft = draftRoles[user.id] !== undefined;
                  return (
                    <TableRow
                      key={user.id}
                      className={`${selectedUserIds.includes(user.id) ? "bg-blue-50 dark:bg-blue-950/50" : ""} ${isDisabledR1 ? "opacity-40 pointer-events-none select-none" : ""} ${isDraft ? "bg-amber-50/50" : ""}`}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedUserIds.includes(user.id)}
                          onCheckedChange={(checked) => handleSelectUser(user.id, checked as boolean)}
                          aria-label={`Select ${user.name}`}
                          disabled={isDisabledR1}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <UserAvatar name={user.name} seed={user.name} avatarUrl={getAvatarUrlByName(user.name)} size="sm" />
                          <span className="font-medium">{user.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-normal">
                          {user.systemRole}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Select
                            value={effectiveRole(user)}
                            onValueChange={(value) => handleRoleChange(user.id, value)}
                            disabled={isDisabledR1}
                          >
                            <SelectTrigger className={`w-[200px] ${isDraft ? 'border-amber-400 ring-1 ring-amber-200' : ''}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {availableForecasterRoles.map(role => (
                                <SelectItem key={role.name} value={role.name}>{role.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {isDraft && (
                            <span className="text-[10px] text-amber-600 font-medium whitespace-nowrap">
                              unsaved
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {/* Footer Save bar — appears when there are pending changes */}
          {hasDraftChanges && (
            <div className="mt-4 pt-4 border-t border-border flex items-center justify-end gap-2">
              <Button variant="outline" onClick={handleDiscardChanges}>
                Discard Changes
              </Button>
              <Button onClick={handleSave}>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Role Change Confirmation Dialog */}
      <AlertDialog open={bulkRoleChangeDialogOpen} onOpenChange={setBulkRoleChangeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change {selectedUserIds.length} User{selectedUserIds.length !== 1 ? 's' : ''} to {bulkRole}?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedUserIds.length <= 10 ? (
                <>
                  The following user{selectedUserIds.length !== 1 ? 's' : ''} will be staged for <strong>{bulkRole}</strong>. Click Save on the main screen to apply.
                  <br /><br />
                  <ul className="list-disc pl-5">
                    {users.filter(u => selectedUserIds.includes(u.id)).map(u => (
                      <li key={u.id}>{u.name}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  <strong>{selectedUserIds.length} users</strong> will be staged for <strong>{bulkRole}</strong>. Click Save on the main screen to apply.
                  <br /><br />
                  <p>First 10 shown:</p>
                  <ul className="list-disc pl-5">
                    {users.filter(u => selectedUserIds.includes(u.id)).slice(0, 10).map(u => (
                      <li key={u.id}>{u.name}</li>
                    ))}
                  </ul>
                  <p className="mt-2">...and {selectedUserIds.length - 10} more</p>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBulkRoleChange}>
              Stage Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
